import React from 'react';
import { Skeleton, Card, Row, Col, Space } from 'antd';

// Skeleton for StatCard
export const StatCardSkeleton = () => (
  <Card
    style={{
      borderTop: '3px solid #e8e8e8',
      height: '100%',
    }}
    bodyStyle={{ padding: '20px' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ flex: 1 }}>
        <Skeleton.Input active size="small" style={{ width: 120, marginBottom: 12 }} />
        <Skeleton.Input active size="large" style={{ width: 80 }} />
        <Skeleton.Input active size="small" style={{ width: 60, marginTop: 8 }} />
      </div>
      <Skeleton.Avatar active size={48} shape="circle" />
    </div>
  </Card>
);

// Skeleton for Dashboard Stats Row
export const DashboardStatsSkeleton = ({ count = 4 }) => (
  <Row gutter={[16, 16]}>
    {[...Array(count)].map((_, index) => (
      <Col key={index} xs={24} sm={12} lg={6}>
        <StatCardSkeleton />
      </Col>
    ))}
  </Row>
);

// Skeleton for Table
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <div className="table-skeleton">
    {/* Header */}
    <div style={{
      display: 'flex',
      gap: 16,
      padding: '16px 0',
      borderBottom: '2px solid #f0f0f0',
      marginBottom: 8
    }}>
      {[...Array(columns)].map((_, i) => (
        <Skeleton.Input
          key={`header-${i}`}
          active
          size="small"
          style={{ width: i === 0 ? 150 : 100 }}
        />
      ))}
    </div>
    {/* Rows */}
    {[...Array(rows)].map((_, rowIndex) => (
      <div
        key={`row-${rowIndex}`}
        style={{
          display: 'flex',
          gap: 16,
          padding: '12px 0',
          borderBottom: '1px solid #f0f0f0',
          animation: `skeleton-fade-in 0.3s ease ${rowIndex * 0.05}s both`
        }}
      >
        {[...Array(columns)].map((_, colIndex) => (
          <Skeleton.Input
            key={`cell-${rowIndex}-${colIndex}`}
            active
            size="small"
            style={{ width: colIndex === 0 ? 150 : colIndex === columns - 1 ? 120 : 100 }}
          />
        ))}
      </div>
    ))}
  </div>
);

// Skeleton for Order Status Chart
export const OrderStatusChartSkeleton = () => (
  <Card
    title={<Skeleton.Input active size="small" style={{ width: 180 }} />}
    style={{ height: '100%' }}
  >
    <Row gutter={[16, 16]}>
      {[...Array(5)].map((_, index) => (
        <Col key={index} xs={24} sm={12}>
          <Card size="small" style={{ borderLeft: '3px solid #e8e8e8' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Skeleton.Input active size="small" style={{ width: '60%' }} />
              <Skeleton.Input active size="default" style={{ width: '40%' }} />
              <Skeleton.Input active size="small" style={{ width: '100%', height: 8 }} />
            </Space>
          </Card>
        </Col>
      ))}
    </Row>
  </Card>
);

// Skeleton for Financial Summary
export const FinancialSummarySkeleton = () => (
  <Card
    title={<Skeleton.Input active size="small" style={{ width: 120 }} />}
    style={{ height: '100%' }}
  >
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <div style={{ textAlign: 'center' }}>
        <Skeleton.Input active size="large" style={{ width: 180 }} />
        <br />
        <Skeleton.Input active size="small" style={{ width: 120, marginTop: 8 }} />
      </div>
      <Row gutter={16}>
        <Col span={12}>
          <Card type="inner" style={{ background: '#f0f2f5' }}>
            <Skeleton.Input active size="small" style={{ width: 60 }} />
            <br />
            <Skeleton.Input active size="default" style={{ width: 100, marginTop: 8 }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card type="inner" style={{ background: '#f0f2f5' }}>
            <Skeleton.Input active size="small" style={{ width: 60 }} />
            <br />
            <Skeleton.Input active size="default" style={{ width: 100, marginTop: 8 }} />
          </Card>
        </Col>
      </Row>
      <Row gutter={16}>
        {[...Array(3)].map((_, i) => (
          <Col key={i} span={8}>
            <Skeleton.Input active size="small" style={{ width: '80%' }} />
            <br />
            <Skeleton.Input active size="default" style={{ width: '60%', marginTop: 8 }} />
          </Col>
        ))}
      </Row>
    </Space>
  </Card>
);

// Skeleton for Recent Activity Timeline
export const RecentActivitySkeleton = () => (
  <Card
    title={<Skeleton.Input active size="small" style={{ width: 220 }} />}
    style={{ height: '100%' }}
  >
    <div style={{ paddingLeft: 24 }}>
      {[...Array(5)].map((_, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 24,
            position: 'relative',
            paddingLeft: 20
          }}
        >
          <Skeleton.Avatar
            active
            size={12}
            shape="circle"
            style={{
              position: 'absolute',
              left: 0,
              top: 4
            }}
          />
          <Space>
            <Skeleton.Input active size="small" style={{ width: 200 }} />
            <Skeleton.Button active size="small" shape="round" style={{ width: 60 }} />
          </Space>
        </div>
      ))}
    </div>
  </Card>
);

// Skeleton for Company/Employee List
export const ListSkeleton = ({ rows = 5 }) => (
  <div>
    <div style={{ marginBottom: 16, textAlign: 'right' }}>
      <Skeleton.Button active style={{ width: 140 }} />
    </div>
    <TableSkeleton rows={rows} columns={5} />
  </div>
);

// Full Dashboard Skeleton
export const DashboardSkeleton = () => (
  <div style={{ padding: 24 }}>
    {/* Header */}
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col flex="auto">
        <Skeleton.Input active size="large" style={{ width: 200 }} />
        <br />
        <Skeleton.Input active size="small" style={{ width: 150, marginTop: 8 }} />
      </Col>
      <Col>
        <Skeleton.Button active style={{ width: 80 }} />
      </Col>
    </Row>

    {/* First Row Stats */}
    <div style={{ marginBottom: 24 }}>
      <DashboardStatsSkeleton count={4} />
    </div>

    {/* Second Row Stats */}
    <div style={{ marginBottom: 24 }}>
      <Row gutter={[16, 16]}>
        {[...Array(3)].map((_, index) => (
          <Col key={index} xs={24} sm={12} lg={6}>
            <StatCardSkeleton />
          </Col>
        ))}
      </Row>
    </div>

    {/* Charts Row */}
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={12}>
        <OrderStatusChartSkeleton />
      </Col>
      <Col xs={24} lg={12}>
        <FinancialSummarySkeleton />
      </Col>
    </Row>

    {/* Activity Row */}
    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
      <Col span={24}>
        <RecentActivitySkeleton />
      </Col>
    </Row>
  </div>
);

